/**
 * @module contexts/motionPreference
 * @description The motion-preference context channel and the hook that reads it
 * @businessContext
 * Decorative keyframes are the one part of the interface a user can turn off,
 * so every animated component reads the preference through this hook.
 *
 * These live apart from MotionProvider because React Fast Refresh can only
 * hot-swap a module whose every export is a component. A hook sitting beside
 * the provider makes the whole file unrefreshable, and this provider wraps the
 * app root — an unrefreshable root costs every consumer its state on each edit.
 */

import { createContext, useContext } from 'react'

/**
 * Type definition for the motion context value
 *
 * @interface MotionContextType
 * @property {boolean} animationsEnabled - Current animation state
 * @businessContext Provides motion configuration for all UI components
 */
export interface MotionContextType {
  /**
   * @property {boolean} animationsEnabled - Current animation state
   * @businessContext Controls all UI animations based on user preference
   * @dataSource User setting or system accessibility preference
   */
  animationsEnabled: boolean
}

export const MotionContext = createContext<MotionContextType | undefined>(
  undefined
)

/**
 * Hook for accessing the motion preference
 *
 * @hook useMotionPreference
 * @returns {MotionContextType} Motion context value
 * @throws {Error} When used outside of MotionProvider
 * @businessLogic Provides access to the motion preference
 *
 * @example
 * ```typescript
 * const { animationsEnabled } = useMotionPreference()
 * ```
 */
export function useMotionPreference(): MotionContextType {
  const context = useContext(MotionContext)
  if (context === undefined) {
    throw new Error('useMotionPreference must be used within a MotionProvider')
  }
  return context
}
